"use client"

import { 
  Box, 
  TextField, 
  MenuItem,
  Typography, 
  FormControl, 
  Select, 
  Card, 
  CardContent,
  Autocomplete,
  Button
} from "@mui/material"
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { es } from 'date-fns/locale'
import { useState, useEffect, useRef, ReactNode } from "react"

export interface FilterOption {
  label: string
  value: string
  type?: 'select' | 'text' | 'search' | 'date' | 'custom'
  options?: { label: string; value: string; disabled?: boolean }[]
  debounce?: number
  minDate?: Date
  maxDate?: Date
  component?: ReactNode
  width?: number
}

interface FiltersSectionProps {
  filters: FilterOption[]
  onFilterChange?: (filterId: string, value: string) => void
  values?: Record<string, string>
  onSearch?: () => void
  searchButtonDisabled?: boolean
}

const parseLocalDate = (dateString: string): Date | null => {
  if (!dateString) return null
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day)
}

const formatLocalDate = (date: Date | null): string => {
  if (!date) return ''
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function FiltersSection({ filters, onFilterChange, values = {}, onSearch, searchButtonDisabled }: FiltersSectionProps) {
  const [textValues, setTextValues] = useState<Record<string, string>>({})
  const [dateValues, setDateValues] = useState<Record<string, Date | null>>({})
  const debounceRef = useRef<NodeJS.Timeout>()

  useEffect(() => {
    const newTextValues: Record<string, string> = {}
    const newDateValues: Record<string, Date | null> = {}
    filters.forEach(filter => {
      if (filter.type === 'text') {
        newTextValues[filter.value] = values[filter.value] || ''
      }
      if (filter.type === 'search') {
        const selectedOption = filter.options?.find(opt => opt.value === values[filter.value])
        newTextValues[filter.value] = selectedOption ? selectedOption.label : ''
      }
      if (filter.type === 'date') {
        const dateValue = values[filter.value]
        if (dateValue) {
          newDateValues[filter.value] = parseLocalDate(dateValue)
        } else {
          newDateValues[filter.value] = null
        }
      }
    })
    setTextValues(newTextValues)
    setDateValues(newDateValues)
  }, [values, filters])

  const handleInputChange = (filterId: string, value: string) => {
    onFilterChange?.(filterId, value)
  }

  const handleTextChange = (filterId: string, value: string, debounceMs?: number) => {
    setTextValues(prev => ({ ...prev, [filterId]: value }))
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!debounceMs || debounceMs === 0) {
      onFilterChange?.(filterId, value)
      return
    }
    debounceRef.current = setTimeout(() => {
      onFilterChange?.(filterId, value)
    }, debounceMs)
  }

  const handleSearchChange = (filterId: string, newValue: { label: string; value: string; disabled?: boolean } | null) => {
    const val = newValue?.value || ''
    onFilterChange?.(filterId, val)
    setTextValues(prev => ({ ...prev, [filterId]: newValue?.label || '' }))
  }

  const handleSearchInputChange = (filterId: string, inputValue: string) => {
    setTextValues(prev => ({ ...prev, [filterId]: inputValue }))
  }

  const handleDateChange = (filterId: string, date: Date | null) => {
    setDateValues(prev => ({ ...prev, [filterId]: date }))
    const formattedDate = formatLocalDate(date)
    onFilterChange?.(filterId, formattedDate)
  }

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Card sx={{ mb: 1 }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {filters.map((filter) => (
              <Box key={filter.value} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  {filter.label}
                </Typography>
                
                {filter.type === 'custom' && filter.component && (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {filter.component}
                  </Box>
                )}

                {filter.type === 'text' && (
                  <TextField
                    size="small"
                    value={textValues[filter.value] || ""}
                    onChange={(e) => handleTextChange(filter.value, e.target.value, filter.debounce)}
                    sx={{ 
                      minWidth: filter.width || 120,
                      '& .MuiInputBase-root': { height: '30px', fontSize: '0.875rem' }
                    }}
                    placeholder={`Buscar ${filter.label.toLowerCase()}...`}
                  />
                )}

                {filter.type === 'search' && (
                  <Autocomplete
                    size="small"
                    options={filter.options || []}
                    getOptionDisabled={(option) => option.disabled === true}
                    // renderOption={(props, option) => (
                    //   <li {...props} style={{ color: option.disabled ? '#aaa' : 'inherit' }}>
                    //     {option.label}
                    //   </li>
                    // )}
                    value={filter.options?.find(opt => opt.value === values[filter.value]) || null}
                    inputValue={textValues[filter.value] || ""}
                    onInputChange={(_, value) => handleSearchInputChange(filter.value, value)}
                    onChange={(_, newValue) => handleSearchChange(filter.value, newValue)}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={`Buscar ${filter.label.toLowerCase()}...`}
                        sx={{ 
                          minWidth: filter.width || 120,
                          '& .MuiInputBase-root': { height: '30px', fontSize: '0.875rem' }
                        }}
                      />
                    )}
                    sx={{ minWidth: filter.width || 120 }}
                    filterOptions={(options, state) => {
                      if (!state.inputValue) return options
                      return options.filter(option =>
                        option.label.toLowerCase().includes(state.inputValue.toLowerCase())
                      )
                    }}
                  />
                )}

                {filter.type === 'date' && (
                  <DatePicker
                    value={dateValues[filter.value] || null}
                    onChange={(date) => handleDateChange(filter.value, date)}
                    minDate={filter.minDate}
                    maxDate={filter.maxDate}
                    timezone="system"
                    disableOpenPicker={false}
                    slotProps={{
                      textField: {
                        size: 'small',
                        sx: { 
                          minWidth: filter.width || 150,
                          '& .MuiInputBase-root': { height: '30px', fontSize: '0.875rem' }
                        },
                        placeholder: 'DD/MM/YYYY'
                      },
                      field: {
                        format: 'dd/MM/yyyy',
                        shouldRespectLeadingZeros: true
                      }
                    }}
                  />
                )}

                {(filter.type === 'select' || !filter.type) && (
                  <FormControl size="small" sx={{ minWidth: filter.width || 120 }}>
                    <Select
                      value={values[filter.value] || ""}
                      onChange={(e) => handleInputChange(filter.value, e.target.value)}
                      sx={{ height: '30px' }}
                      renderValue={(selected) => {
                        const opt = filter.options?.find(o => o.value === selected);
                        return opt?.label || '';
                      }}
                    >
                      {filter.options?.map((option) => (
                        <MenuItem 
                          key={option.value} 
                          value={option.value}
                          disabled={option.disabled}
                          sx={{ color: option.disabled ? '#aaa' : 'inherit' }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            ))}

            {onSearch && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                  &nbsp;
                </Typography>
                <Button
                  variant="contained"
                  onClick={searchButtonDisabled ? undefined : onSearch}
                  size="small"
                  sx={{ height: '30px', textTransform: 'none' }}
                  disabled={searchButtonDisabled}
                >
                  Buscar
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>
    </LocalizationProvider>
  )
}